// src/components/PairingsList.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  likePairing,
  dislikePairing,
  voteOnPairing,          // <-- we import voteOnPairing now
  savePlaceAsFavorite,
  removePlaceAsFavorite,
} from '../services/api';
import { usePlaces } from '../context/PlaceContext';
import { useAuth } from '../context/AuthContext';
import ThumbsUpIcon from './icons/ThumbsUpIcon';
import ThumbsDownIcon from './icons/ThumbsDownIcon';
import HeartIcon from './icons/HeartIcon';
import './PairingsList.css';

const PairingsList = ({ pairings, loading, userId, primaryPlaceId }) => {
  const {
    userVotes,
    loadUserVotes,
    loadUserFavorites,
    savedPlaces,
  } = usePlaces();

  const { role, getToken } = useAuth();
  const isLoggedIn = !!userId;
  const canUseWeightedVotes = (role === 'super_user' || role === 'admin');

  const [superWeightMap, setSuperWeightMap] = useState({});

  // Helper: build a Google Maps URL based on name + address
  const buildGoogleMapsUrl = (placeName, address) => {
    const query = encodeURIComponent(`${placeName} ${address}`);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  const handleVote = async (pairingId, type) => {
    if (!isLoggedIn) {
      alert('You need to be signed in to vote on pairings.');
      return;
    }
    try {
      const token = getToken();
      let response;
      if (type === 'like') {
        response = await likePairing(token, pairingId);
      } else {
        response = await dislikePairing(token, pairingId);
      }
      if (response.error) {
        console.error('[PairingsList] handleVote error:', response.error);
      } else {
        await loadUserVotes();
      }
    } catch (error) {
      console.error('[PairingsList] handleVote error:', error.message);
    }
  };

  const handleToggleFavorite = async (placeInternalId, isFavorited) => {
    if (!isLoggedIn) {
      alert('You must be logged in to save/un-save a place.');
      return;
    }
    try {
      const token = getToken();
      if (isFavorited) {
        const confirmed = window.confirm('Remove this place from favorites?');
        if (!confirmed) return;
        await removePlaceAsFavorite(token, placeInternalId);
      } else {
        await savePlaceAsFavorite(token, placeInternalId);
      }
      await loadUserFavorites();
    } catch (error) {
      console.error('[PairingsList] handleToggleFavorite error:', error.message);
    }
  };

  /**
   * Replaced the inline fetch call with voteOnPairing(token, pairingId, voteType, weight).
   */
  const handleWeightChange = async (pairingId, newWeight, voteType) => {
    if (!isLoggedIn) {
      alert('You must be logged in to adjust vote weight.');
      return;
    }
    try {
      setSuperWeightMap((prev) => ({
        ...prev,
        [pairingId]: newWeight,
      }));

      const token = getToken();
      await voteOnPairing(token, pairingId, voteType, parseInt(newWeight, 10));

      await loadUserVotes();
    } catch (err) {
      console.error('[PairingsList] handleWeightChange error:', err.message);
    }
  };

  if (loading) {
    return <p>Loading pairings...</p>;
  }
  if (!pairings || !pairings.length) {
    return <p>No pairings yet.</p>;
  }

  return (
    <ul className="pairings-list">
      {pairings.map((pairing) => {
        const pairingId = Number(pairing.id);
        const userVote = userVotes[pairingId] || {};
        const { voteType, weight } = userVote;
        const currentWeight = weight ?? 1;

        const isLiked = voteType === 'like';
        const isDisliked = voteType === 'dislike';

        // figure out the "other" place for favorites
        const otherPlaceId =
          primaryPlaceId === pairing.place1_internal_id
            ? pairing.place2_internal_id
            : pairing.place1_internal_id;

        const isFavorited = savedPlaces.some(
          (fav) => String(fav.place_internal_id) === String(otherPlaceId)
        );

        const userHasVoted = !!voteType;
        const currentVoteType = isLiked ? 'like' : isDisliked ? 'dislike' : null;

        // Build a Google Maps link for each place
        const googleMapsUrl = buildGoogleMapsUrl(pairing.name, pairing.address || '');

        return (
          <li key={pairingId} className="pairing-item">
            <strong>
              {pairing.slug ? (
                <Link to={`/p/${pairing.slug}`} className="custom-link">
                  {pairing.name || 'Unknown Place'}
                </Link>
              ) : (
                pairing.name || 'Unknown Place'
              )}
            </strong>
            <br />
            {pairing.address || 'Address not available'}

            {/* If there's an address, show a subtle link like "(maps)" */}
            {pairing.address && (
              <span style={{ marginLeft: '0.25rem' }}>
                <a
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="maps-link"
                >
                  (Google Maps)
                </a>
              </span>
            )}

            <span className="icon-group">
              {/* Thumbs Up */}
              <button
                onClick={() => handleVote(pairingId, 'like')}
                className="icon-btn"
              >
                <ThumbsUpIcon isSelected={isLiked} />
              </button>

              {/* Thumbs Down */}
              <button
                onClick={() => handleVote(pairingId, 'dislike')}
                className="icon-btn"
              >
                <ThumbsDownIcon isSelected={isDisliked} />
              </button>

              {/* Favorite Heart */}
              <button
                onClick={() => handleToggleFavorite(otherPlaceId, isFavorited)}
                className="icon-btn"
              >
                <HeartIcon isSelected={isFavorited} />
              </button>
            </span>

            {/* Weight selector for admins/super_users when a vote is active */}
            {canUseWeightedVotes && userHasVoted && currentVoteType && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                marginTop: '8px'
              }}>
                <span style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--dynamic-secondary-text)',
                  marginBottom: '4px'
                }}>
                  Vote weight: {superWeightMap[pairingId] ?? currentWeight}
                </span>
                
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px', 
                  justifyContent: 'center' 
                }}>
                  {[1, 2, 5, 10, 20, 50, 100].map((val) => (
                    <button
                      key={val}
                      onClick={() => handleWeightChange(pairingId, val, currentVoteType)}
                      style={{
                        padding: '6px 12px',
                        fontSize: '0.75rem',
                        background: (superWeightMap[pairingId] ?? currentWeight) === val 
                          ? 'var(--active-button-background)' 
                          : 'var(--dynamic-secondary-background)',
                        color: (superWeightMap[pairingId] ?? currentWeight) === val 
                          ? 'var(--active-button-foreground)' 
                          : 'var(--dynamic-text)',
                        border: 'none',
                        borderRadius: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      {val}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default PairingsList;